import { useCallback, useMemo, useState } from "react";
// import styles from "./index.module.scss";
import { ModalBase } from "../ModalBase";
import { Wizard } from "components/wizards/Wizard";
import { setActiveModal } from "store/modal/slice";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { SelectDestinationStep } from "./steps/SelectDestination";
import { PlatformSettingsStep } from "./steps/PlatformSettingsStep";
import { LivestreamDetailsStep } from "./steps/LivestreamDetailsStep";
import { ProcessingStep } from "./steps/ProcessingStep";
import { PlatformId } from "views/page-content/platforms/types";

export enum NewLivestreamModalSteps {
    SelectDestination = "select-destination",
    PlatformSettings = "platform-settings",
    LivestreamDetails = "livestream-details",
    Processing = "processing"
}

export const NewLivestreamModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [activeStep, setActiveStep] = useState<NewLivestreamModalSteps>(
        NewLivestreamModalSteps.SelectDestination
    );

    const [selectedPlatform, setSelectedPlatform] = useState<PlatformId | null>(
        null
    );

    const canSubmit = useMemo(() => {
        return true;
    }, []);

    const advanceStep = useCallback(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                setActiveStep(NewLivestreamModalSteps.LivestreamDetails);
                break;
            case NewLivestreamModalSteps.PlatformSettings:
                setActiveStep(NewLivestreamModalSteps.SelectDestination);
                break;
            case NewLivestreamModalSteps.LivestreamDetails:
                setActiveStep(NewLivestreamModalSteps.Processing);
                break;
            case NewLivestreamModalSteps.Processing:
                if (canSubmit) {
                    // TODO: reset form state
                    setActiveStep(NewLivestreamModalSteps.SelectDestination);
                } else {
                    setActiveStep(NewLivestreamModalSteps.LivestreamDetails);
                }
                break;
        }
    }, [activeStep, canSubmit]);

    const advanceButtonText = useMemo(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                return t("misc:next");
            case NewLivestreamModalSteps.PlatformSettings:
                return t("buttons:save");
            case NewLivestreamModalSteps.LivestreamDetails:
                return t("buttons:schedule-event");
            case NewLivestreamModalSteps.Processing:
                if (canSubmit) {
                    return t("buttons:schedule-another");
                } else {
                    return t("buttons:back");
                }
            default:
                return t("buttons:back");
        }
    }, [activeStep, t, canSubmit]);

    const dismissButtonText = useMemo(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
            case NewLivestreamModalSteps.PlatformSettings:
                return t("buttons:cancel");
            case NewLivestreamModalSteps.LivestreamDetails:
                return t("buttons:back");
            case NewLivestreamModalSteps.Processing:
                if (canSubmit) {
                    return t("buttons:done");
                } else {
                    return t("buttons:cancel");
                }
            default:
                return t("buttons:cancel");
        }
    }, [t, activeStep, canSubmit]);

    const dismissButtonAction = useCallback(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                dispatch(setActiveModal(null));
                break;
            case NewLivestreamModalSteps.PlatformSettings:
                setActiveStep(NewLivestreamModalSteps.SelectDestination);
                break;
            case NewLivestreamModalSteps.LivestreamDetails:
                setActiveStep(NewLivestreamModalSteps.SelectDestination);
                break;
            case NewLivestreamModalSteps.Processing:
                dispatch(setActiveModal(null));
                setActiveStep(NewLivestreamModalSteps.SelectDestination);
                break;
        }
    }, [activeStep, dispatch]);

    const canProceedToNextStep = useMemo(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                return true; // Add actual validation logic here
            case NewLivestreamModalSteps.PlatformSettings:
                return true; // Add actual validation logic here
            case NewLivestreamModalSteps.LivestreamDetails:
                return true; // Add actual validation logic here
            case NewLivestreamModalSteps.Processing:
                return canSubmit; // Add actual validation logic here
            default:
                return false;
        }
    }, [activeStep, canSubmit]);

    return (
        <ModalBase
            isOpen
            header={t("platforms:new-livestream")}
            successButton={advanceButtonText}
            onSuccess={advanceStep}
            dismissButton={dismissButtonText}
            onDismiss={dismissButtonAction}
            successDisabled={!canProceedToNextStep}
            shouldNotCloseOnSuccess={true}
        >
            <div>
                <Wizard<NewLivestreamModalSteps>
                    activeStep={activeStep}
                    steps={[
                        {
                            id: NewLivestreamModalSteps.SelectDestination,
                            component: (
                                <SelectDestinationStep
                                    setSelectedPlatform={setSelectedPlatform}
                                    advanceToPlatformSettings={() => {
                                        setActiveStep(
                                            NewLivestreamModalSteps.PlatformSettings
                                        );
                                    }}
                                />
                            )
                        },
                        {
                            id: NewLivestreamModalSteps.PlatformSettings,
                            component: (
                                <PlatformSettingsStep
                                    selectedPlatform={selectedPlatform}
                                />
                            )
                        },
                        {
                            id: NewLivestreamModalSteps.LivestreamDetails,
                            component: <LivestreamDetailsStep />
                        },
                        {
                            id: NewLivestreamModalSteps.Processing,
                            component: <ProcessingStep />
                        }
                    ]}
                />
            </div>
        </ModalBase>
    );
};
